import React, { useState, useEffect } from 'react';

const CalendarView = ({ activities, selectedDate, filters, onDateSelect, onActivitySelect, onFilterChange }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [calendarData, setCalendarData] = useState({});

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const activityCategories = {
    '學術': { color: 'blue', icon: '📚' },
    '體育': { color: 'green', icon: '🏃' },
    '藝術': { color: 'purple', icon: '🎨' },
    '社會服務': { color: 'yellow', icon: '🤝' },
    '領袖訓練': { color: 'red', icon: '👑' },
    '考試': { color: 'darkred', icon: '📝' },
    '假期': { color: 'darkgreen', icon: '🎊' },
    '典禮': { color: 'darkblue', icon: '🏛️' },
    '競賽': { color: 'orange', icon: '🏆' }
  };

  const processCalendarData = React.useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const data = {};

    // Group activities by date
    activities.forEach(activity => {
      if (activity.activity_date) {
        const activityDate = new Date(activity.activity_date);
        if (activityDate.getFullYear() === year && activityDate.getMonth() === month) {
          const dateStr = activity.activity_date;
          if (!data[dateStr]) {
            data[dateStr] = [];
          }
          data[dateStr].push(activity);
        }
      }
    });

    setCalendarData(data);
  }, [activities, currentDate, filters]);

  useEffect(() => {
    processCalendarData();
  }, [processCalendarData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    if (date) {
      onDateSelect(date);
    }
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    
    if (dateStr === today) return 'today';
    
    const dayData = calendarData[dateStr];
    if (dayData && dayData.length > 0) {
      return 'has-activities';
    }
    
    return 'normal';
  };

  const getDateActivities = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return calendarData[dateStr] || [];
  };

  const getCategoryColor = (category) => {
    return activityCategories[category]?.color || 'gray';
  };

  const getCategoryIcon = (category) => {
    return activityCategories[category]?.icon || '📋';
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="calendar-view">
      <div className="calendar-controls">
        <div className="year-selector">
          <label>年份:</label>
          <select 
            value={filters.year} 
            onChange={(e) => onFilterChange({ year: parseInt(e.target.value) })}
            className="year-select"
          >
            {[2023, 2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
        </div>

        <div className="category-filter">
          <label>類別篩選:</label>
          <select 
            value={filters.category} 
            onChange={(e) => onFilterChange({ category: e.target.value })}
            className="category-select"
          >
            <option value="all">全部類別</option>
            {Object.keys(activityCategories).map(category => (
              <option key={category} value={category}>
                {activityCategories[category].icon} {category}
              </option>
            ))}
          </select>
        </div>

        <div className="grade-filter">
          <label>學級篩選:</label>
          <select 
            value={filters.grade} 
            onChange={(e) => onFilterChange({ grade: e.target.value })}
            className="grade-select"
          >
            <option value="all">全部學級</option>
            {['1', '2', '3', '4', '5', '6'].map(grade => (
              <option key={grade} value={grade}>中{grade}</option>
            ))}
          </select>
        </div>

        <div className="month-filter">
          <label>月份篩選:</label>
          <select 
            value={filters.month} 
            onChange={(e) => onFilterChange({ month: e.target.value })}
            className="month-select"
          >
            <option value="all">全部月份</option>
            {monthNames.map((month, index) => (
              <option key={index} value={index + 1}>{month}</option>
            ))}
          </select>
        </div>

        <button 
          className="slp-management-btn"
          onClick={() => {/* Navigate to SLP management */}}
        >
          SLP管理
        </button>
      </div>

      <div className="calendar-navigation">
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(-1)}
        >
          ◀
        </button>
        <h3>{currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}</h3>
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(1)}
        >
          ▶
        </button>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-body">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const status = getDateStatus(date);
            const dayActivities = getDateActivities(date);
            const isSelected = selectedDate && date.toISOString().split('T')[0] === selectedDate.toISOString().split('T')[0];

            return (
              <div 
                key={index} 
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                
                {dayActivities.length > 0 && (
                  <div className="day-activities">
                    {dayActivities.slice(0, 3).map((activity, actIndex) => (
                      <div 
                        key={actIndex} 
                        className={`activity-preview ${getCategoryColor(activity.activity_category)}`}
                        title={`${activity.activity_name} - ${activity.start_time}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onActivitySelect(activity);
                        }}
                      >
                        <span className="activity-icon">
                          {getCategoryIcon(activity.activity_category)}
                        </span>
                        <span className="activity-name">
                          {activity.activity_name}
                        </span>
                      </div>
                    ))}
                    {dayActivities.length > 3 && (
                      <div className="more-activities">
                        +{dayActivities.length - 3} 更多
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend">
        <h4>活動類別圖例</h4>
        <div className="legend-items">
          {Object.entries(activityCategories).map(([category, info]) => (
            <div key={category} className="legend-item">
              <span className="legend-icon">{info.icon}</span>
              <span className="legend-color" style={{ backgroundColor: `var(--${info.color}-color)` }}></span>
              <span className="legend-label">{category}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
